import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiBox,
  FiPackage,
  FiChevronRight,
  FiChevronDown,
  FiX,
  FiLayers,
  FiSettings,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import "./InventoryTree.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const formatWeight = (value, unit) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const shown = Number.isInteger(n) ? String(n) : String(n);
  const u = String(unit || "").trim();
  return u ? `${shown} ${u}` : shown;
};

const formatCycleTime = (value) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  if (n <= 0) return null;
  const shown = Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  return `${shown} sn`;
};

function nodeMatches(node, q) {
  if (!q) return true;
  const low = q.toLowerCase();
  return [
    node.part?.name,
    node.part?.code,
    node.model?.name,
    node.model?.code,
    ...(node.machines || []).map((m) => m?.name),
    ...(node.machines || []).map((m) => m?.code),
  ].some((s) =>
    String(s || "")
      .toLowerCase()
      .includes(low),
  );
}

function hasMatchingDescendant(node, q) {
  if (!q || nodeMatches(node, q)) return true;
  return (node.children || []).some((c) => hasMatchingDescendant(c, q));
}

function TreeNode({
  node,
  depth,
  expanded,
  onToggle,
  searchQuery,
  quantityUsedByParent,
}) {
  const hasChildren = node.children?.length > 0;
  const isOpen = searchQuery ? true : expanded[node._id] === true;
  const filteredChildren = !searchQuery
    ? node.children || []
    : (node.children || []).filter((c) =>
        hasMatchingDescendant(c, searchQuery),
      );
  const showChildren = filteredChildren.length > 0 && (isOpen || searchQuery);

  const handleRowClick = () => {
    hasChildren && onToggle(node._id);
  };

  const isModelHub = node.nodeKind === "model";
  const isFinalProduct = node.nodeKind === "final-product";
  const isAssemblyInput = node.nodeKind === "assembly-input";
  const partName = isModelHub
    ? node.model?.name || "Ürün modeli"
    : node.part?.name || node._id;
  const partCode = isModelHub ? node.model?.code : node.part?.code;
  const hasInventoryRecord = !!node.inventoryItem;
  const stock = isModelHub ? null : Number(node.inventoryItem?.quantity ?? 0);
  const unitWeightLabel = formatWeight(node.unitWeight, node.weightUnit);
  const wastageAmount = Number(node.wastage ?? node.assemblyWastage) || 0;
  const wastageLabel =
    wastageAmount > 0 ? formatWeight(wastageAmount, node.weightUnit) : null;
  const parentQty = Number(quantityUsedByParent);
  const hasParentQty =
    quantityUsedByParent != null && Number.isFinite(parentQty) && parentQty > 0;
  const effectiveWeight =
    unitWeightLabel != null && hasParentQty
      ? formatWeight(Number(node.unitWeight) * parentQty, node.weightUnit)
      : null;
  const unitCycleLabel = formatCycleTime(node.unitCycleTime);
  const effectiveCycle =
    unitCycleLabel != null && hasParentQty
      ? formatCycleTime(Number(node.unitCycleTime) * parentQty)
      : null;

  return (
    <div
      className={`inventory-tree-node ${hasChildren ? "has-children" : ""} ${isOpen ? "open" : ""}`}
    >
      <div
        className="tree-row"
        onClick={handleRowClick}
      >
        <span className="tree-toggle">
          {hasChildren ? (
            isOpen ? (
              <FiChevronDown size={16} />
            ) : (
              <FiChevronRight size={16} />
            )
          ) : (
            <span className="tree-spacer" />
          )}
        </span>

        {isModelHub ? (
          <FiLayers size={20} className="tree-icon tree-icon-model" />
        ) : isFinalProduct ? (
          <FiSettings size={20} className="tree-icon tree-icon-final-product" />
        ) : isAssemblyInput ? (
          <FiPackage size={20} className="tree-icon tree-icon-assembly-input" />
        ) : hasChildren ? (
          <FiBox size={20} className="tree-icon tree-icon-folder" />
        ) : (
          <FiPackage size={20} className="tree-icon tree-icon-leaf" />
        )}

        <span className="tree-label">
          <span>{partName}</span>
          {partCode && (
            <span
              style={{
                marginLeft: 8,
                fontFamily: "monospace",
                opacity: 0.7,
                fontSize: "0.85em",
              }}
            >
              {partCode}
            </span>
          )}
          {quantityUsedByParent != null && (
            <span className="tree-composition"> — ×{quantityUsedByParent}</span>
          )}
        </span>

        <span className="tree-row-meta">
          {unitWeightLabel != null && (
            <span
              className="tree-weight"
              title={
                isModelHub
                  ? "Model toplamı: nihai ürün + montaj girdileri (montaj fireleri düşülmüş)"
                  : effectiveWeight
                    ? `Birim: ${unitWeightLabel}, üst adımda: ${effectiveWeight}`
                    : isAssemblyInput
                      ? "Montaj girdisi birim ağırlığı"
                      : "Karşılık ağırlığı (hammadde / üretim)"
              }
            >
              {unitWeightLabel}
              {wastageLabel ? (
                <span
                  className="tree-weight-wastage"
                  title={
                    isAssemblyInput
                      ? "Montaj satır firesi"
                      : "Üretim firesi (çevrim başına)"
                  }
                >
                  {" "}
                  −{wastageLabel}
                </span>
              ) : null}
            </span>
          )}
          {unitCycleLabel != null && (
            <span
              className="tree-cycle"
              title={
                isModelHub
                  ? "Model toplamı: nihai ürün + montaj girdileri çevrim süreleri"
                  : effectiveCycle
                    ? `Birim: ${unitCycleLabel}, üst adımda: ${effectiveCycle}`
                    : isAssemblyInput
                      ? "Montaj girdisi çevrim süresi (alt üretim dahil)"
                      : "Birim çevrim süresi (kendi net çevrim + alt üretim)"
              }
            >
              {unitCycleLabel}
            </span>
          )}
          {stock != null && (
            <span
              className={`tree-count${!hasInventoryRecord ? " tree-count--empty" : ""}`}
              title={
                hasInventoryRecord
                  ? "Stok miktarı"
                  : "Envanter kaydı yok — stok 0 kabul edilir"
              }
            >
              stok: {stock}
            </span>
          )}
        </span>
      </div>

      {showChildren && (
        <div className="tree-children">
          {filteredChildren.map((child) => (
            <TreeNode
              key={child._id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              searchQuery={searchQuery}
              quantityUsedByParent={child.consumptionRatio}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const InventoryTree = () => {
  useTitle("Rovion MES - Ürün Ağacı");
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [treeRoots, setTreeRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = "info") => {
    const newToast = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== newToast.id)),
      3000,
    );
  };

  const fetchData = async () => {
    setLoading(true);
    const h = { Authorization: `Bearer ${getToken()}` };
    try {
      const res = await axios.get(`${API_URL}/api/inventory/tree`, {
        headers: h,
      });
      setTreeRoots(Array.isArray(res.data?.roots) ? res.data.roots : []);
    } catch {
      addToast("Veriler yüklenemedi", "danger");
      setTreeRoots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleNode = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const closeAll = () => setExpanded({});

  return (
    <AppPage
      title="Ürün Ağacı"
      subtitle="Modele göre üretim ve paketleme adımları"
      icon={<FiPackage />}
    >
      <CardContainer>
        {loading ? (
          <div className="inventory-tree-loading">
            <div className="loading-spinner" />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <div className="inventory-tree-toolbar d-flex justify-content-between align-items-center">
              <Button
                appearance="primary-subtle"
                icon={<FiX />}
                onClick={closeAll}
                disabled={Object.keys(expanded || {}).length === 0}
              >
                Tümünü Kapat
              </Button>
            </div>

            <div className="inventory-tree">
              {treeRoots.length === 0 ? (
                <div className="inventory-tree-empty">
                  <FiPackage size={64} />
                  <h3>Ürün Ağacı Boş</h3>
                  <p>
                    Henüz ilişki tanımlanmamış.{" "}
                    <span
                      style={{
                        color: "var(--m-link-color, #3b82f6)",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/part-machines")}
                    >
                      Parça / Makine
                    </span>
                    {" veya "}
                    <span
                      style={{
                        color: "var(--m-link-color, #3b82f6)",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/part-assemblies")}
                    >
                      Montaj Parçaları
                    </span>
                  </p>
                </div>
              ) : (
                <div className="tree-list">
                  {treeRoots.map((node) => (
                    <TreeNode
                      key={node._id}
                      node={node}
                      depth={0}
                      expanded={expanded}
                      onToggle={toggleNode}
                      searchQuery={searchQuery}
                      quantityUsedByParent={null}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(InventoryTree, ["admin", "employee", "user"]);
